import { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import app, { db } from "../../../firebase";
import { useAuth } from "contexts/AuthContext";
import { propertyService } from "services/firestoreService";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";

import SyncIcon from "@mui/icons-material/Sync";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import GavelIcon from "@mui/icons-material/Gavel";

const functions = getFunctions(app);

const getStatusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("open") || s.includes("active")) return "error";
  if (s.includes("hearing") || s.includes("pending")) return "warning";
  if (s.includes("resolved") || s.includes("closed") || s.includes("dismissed")) return "success";
  return "info";
};

const getAgencyColor = (agency) => {
  const colors = { HPD: "#e53935", DOB: "#1e88e5", ECB: "#f57c00", FDNY: "#d32f2f", DEP: "#43a047", DSNY: "#8e24aa", "311": "#00acc1" };
  return colors[agency] || "#757575";
};

function Violations() {
  const { user } = useAuth();

  const [tab, setTab] = useState(0); // 0=violations, 1=complaints, 2=permits, 3=311
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("all");
  const [agencyFilter, setAgencyFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");

  const [violations, setViolations] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [permits, setPermits] = useState([]);
  const [threeEleven, setThreeEleven] = useState([]);

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState("");

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  // Load properties + existing NYC data
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const props = await propertyService.getAll(user.uid);
        setProperties(props);
        await loadNycData();
      } catch (err) {
        setError("Failed to load data: " + err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const loadNycData = async () => {
    const uid = user.uid;
    const [vSnap, cSnap, pSnap, tSnap] = await Promise.all([
      getDocs(query(collection(db, "nycViolations"), where("userId", "==", uid))),
      getDocs(query(collection(db, "nycComplaints"), where("userId", "==", uid))),
      getDocs(query(collection(db, "nycPermits"), where("userId", "==", uid))),
      getDocs(query(collection(db, "nyc311"), where("userId", "==", uid))),
    ]);
    setViolations(vSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setComplaints(cSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setPermits(pSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setThreeEleven(tSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  // Sync a property's NYC data
  const handleSync = async (property) => {
    if (!property.boro || !property.block || !property.lot) {
      setError(`Property "${property.name || property.address}" is missing BBL (Borough/Block/Lot). Edit the property to add these fields.`);
      return;
    }

    setSyncing(true);
    setSyncResult(null);
    setError("");

    try {
      const syncFn = httpsCallable(functions, "nycSyncProperty");
      const result = await syncFn({
        propertyId: property.id,
        boro: property.boro,
        block: property.block,
        lot: property.lot,
        address: property.address || "",
      });

      // Also sync 311 if address exists
      if (property.address) {
        const sync311 = httpsCallable(functions, "nycSync311");
        await sync311({
          propertyId: property.id,
          address: property.address,
          borough: property.borough || property.boro || "",
        });
      }

      setSyncResult(result.data);
      await loadNycData(); // Refresh
    } catch (err) {
      setError("Sync failed: " + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    setError("");
    let totalResults = { violations: 0, complaints: 0, permits: 0 };

    for (const prop of properties) {
      if (!prop.boro || !prop.block || !prop.lot) continue;
      try {
        const syncFn = httpsCallable(functions, "nycSyncProperty");
        const result = await syncFn({
          propertyId: prop.id, boro: prop.boro, block: prop.block, lot: prop.lot, address: prop.address || "",
        });
        totalResults.violations += result.data.violations || 0;
        totalResults.complaints += result.data.complaints || 0;
        totalResults.permits += result.data.permits || 0;
      } catch (err) {
        console.error(`Sync failed for ${prop.address}:`, err);
      }
    }

    setSyncResult(totalResults);
    await loadNycData();
    setSyncing(false);
  };

  // Filter logic
  const filterItems = (items) => {
    return items.filter((item) => {
      const matchesProp = selectedProperty === "all" || item.propertyId === selectedProperty;
      const matchesAgency = agencyFilter === "All" || item.agency === agencyFilter;
      const matchesStatus = statusFilter === "All" || (item.status || "").toLowerCase().includes(statusFilter.toLowerCase());
      const matchesSearch = !search ||
        (item.description || "").toLowerCase().includes(search.toLowerCase()) ||
        (item.address || "").toLowerCase().includes(search.toLowerCase());
      return matchesProp && matchesAgency && matchesStatus && matchesSearch;
    });
  };

  const currentData = tab === 0 ? filterItems(violations) : tab === 1 ? filterItems(complaints) : tab === 2 ? filterItems(permits) : filterItems(threeEleven);
  const tabLabels = [`Violations (${violations.length})`, `Complaints (${complaints.length})`, `Permits (${permits.length})`, `311 (${threeEleven.length})`];

  // Stats
  const openViolations = violations.filter((v) => {
    const s = (v.status || "").toLowerCase();
    return s.includes("open") || s.includes("active") || s.includes("pending");
  }).length;

  const byAgency = {};
  violations.forEach((v) => { byAgency[v.agency] = (byAgency[v.agency] || 0) + 1; });

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </ArgonBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        {/* Header */}
        <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <ArgonBox>
            <ArgonTypography variant="h4" fontWeight="bold">NYC Compliance</ArgonTypography>
            <ArgonTypography variant="body2" color="text">
              Violations, complaints, permits, and 311 data from NYC Open Data
            </ArgonTypography>
          </ArgonBox>
          <ArgonBox display="flex" gap={1}>
            <ArgonButton
              color="dark"
              size="small"
              onClick={handleSyncAll}
              disabled={syncing || properties.length === 0}
              startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            >
              {syncing ? "Syncing..." : "Sync All Properties"}
            </ArgonButton>
          </ArgonBox>
        </ArgonBox>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setError("")}>{error}</Alert>}
        {syncResult && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setSyncResult(null)}>
            Sync complete: {syncResult.violations} violations, {syncResult.complaints} complaints, {syncResult.permits} permits
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} md={3}>
            <Card>
              <ArgonBox p={2} textAlign="center">
                <ErrorIcon sx={{ fontSize: 28, color: "#e53935", mb: 0.5 }} />
                <ArgonTypography variant="h4" fontWeight="bold">{violations.length}</ArgonTypography>
                <ArgonTypography variant="caption" color="text">Total Violations</ArgonTypography>
              </ArgonBox>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <ArgonBox p={2} textAlign="center">
                <WarningIcon sx={{ fontSize: 28, color: "#f57c00", mb: 0.5 }} />
                <ArgonTypography variant="h4" fontWeight="bold">{openViolations}</ArgonTypography>
                <ArgonTypography variant="caption" color="text">Open / Active</ArgonTypography>
              </ArgonBox>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <ArgonBox p={2} textAlign="center">
                <GavelIcon sx={{ fontSize: 28, color: "#1e88e5", mb: 0.5 }} />
                <ArgonTypography variant="h4" fontWeight="bold">{complaints.length}</ArgonTypography>
                <ArgonTypography variant="caption" color="text">Complaints</ArgonTypography>
              </ArgonBox>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <ArgonBox p={2} textAlign="center">
                <CheckCircleIcon sx={{ fontSize: 28, color: "#43a047", mb: 0.5 }} />
                <ArgonTypography variant="h4" fontWeight="bold">{permits.length}</ArgonTypography>
                <ArgonTypography variant="caption" color="text">Permits</ArgonTypography>
              </ArgonBox>
            </Card>
          </Grid>
        </Grid>

        {/* Agency breakdown chips */}
        {Object.keys(byAgency).length > 0 && (
          <ArgonBox mb={3} display="flex" gap={1} flexWrap="wrap">
            {Object.entries(byAgency).map(([agency, count]) => (
              <Chip
                key={agency}
                label={`${agency}: ${count}`}
                sx={{ backgroundColor: getAgencyColor(agency), color: "white", fontWeight: 600 }}
                onClick={() => setAgencyFilter(agency === agencyFilter ? "All" : agency)}
              />
            ))}
          </ArgonBox>
        )}

        {/* Main Content */}
        <Card>
          <ArgonBox px={3} pt={2}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)}>
              {tabLabels.map((label, i) => <Tab key={i} label={label} />)}
            </Tabs>
          </ArgonBox>
          <Divider />

          {/* Filters */}
          <ArgonBox px={3} pt={2} pb={1}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <ArgonInput
                  placeholder="Search..."
                  icon={{ component: "search", direction: "left" }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Property</InputLabel>
                  <Select value={selectedProperty} label="Property" onChange={(e) => setSelectedProperty(e.target.value)}>
                    <MenuItem value="all">All Properties</MenuItem>
                    {properties.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name || p.address || p.id}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Agency</InputLabel>
                  <Select value={agencyFilter} label="Agency" onChange={(e) => setAgencyFilter(e.target.value)}>
                    <MenuItem value="All">All Agencies</MenuItem>
                    <MenuItem value="HPD">HPD</MenuItem>
                    <MenuItem value="DOB">DOB</MenuItem>
                    <MenuItem value="ECB">ECB</MenuItem>
                    <MenuItem value="FDNY">FDNY</MenuItem>
                    <MenuItem value="DEP">DEP</MenuItem>
                    <MenuItem value="311">311</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="hearing">In Hearing</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {selectedProperty !== "all" && (
                <Grid item xs={6} md={2}>
                  <ArgonButton
                    color="info"
                    size="small"
                    fullWidth
                    onClick={() => {
                      const prop = properties.find((p) => p.id === selectedProperty);
                      if (prop) handleSync(prop);
                    }}
                    disabled={syncing}
                  >
                    <SyncIcon sx={{ mr: 0.5 }} /> Sync This Property
                  </ArgonButton>
                </Grid>
              )}
            </Grid>
          </ArgonBox>

          {/* Table */}
          <ArgonBox p={3}>
            {currentData.length === 0 ? (
              <ArgonBox textAlign="center" py={5}>
                <ArgonTypography variant="h6" color="text">
                  {violations.length === 0 && complaints.length === 0
                    ? "No NYC data yet. Add BBL info to your properties and click Sync."
                    : "No results match your filters."}
                </ArgonTypography>
              </ArgonBox>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Agency</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      {tab === 0 && <TableCell>Severity</TableCell>}
                      {tab === 2 && <TableCell>Type</TableCell>}
                      <TableCell align="center">Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentData.slice(0, 100).map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Chip
                            label={item.agency}
                            size="small"
                            sx={{ backgroundColor: getAgencyColor(item.agency), color: "white", fontWeight: 600, fontSize: "0.7rem" }}
                          />
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption" sx={{ maxWidth: 300, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item.description || item.type || "-"}
                          </ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption">{item.address || "-"}</ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption">
                            {item.date ? new Date(item.date).toLocaleDateString() : "-"}
                          </ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <Chip label={item.status || "Unknown"} color={getStatusColor(item.status)} size="small" />
                        </TableCell>
                        {tab === 0 && (
                          <TableCell>
                            <ArgonTypography variant="caption">{item.severity || "-"}</ArgonTypography>
                          </TableCell>
                        )}
                        {tab === 2 && (
                          <TableCell>
                            <ArgonTypography variant="caption">{item.type || "-"}</ArgonTypography>
                          </TableCell>
                        )}
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => { setDetailItem(item); setDetailOpen(true); }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {currentData.length > 100 && (
              <ArgonTypography variant="caption" color="text" mt={2} display="block" textAlign="center">
                Showing 100 of {currentData.length} results
              </ArgonTypography>
            )}
          </ArgonBox>
        </Card>
      </ArgonBox>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {detailItem?.agency} — {detailItem?.datasetLabel || "Record Details"}
        </DialogTitle>
        <DialogContent>
          {detailItem && (
            <ArgonBox mt={1}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <ArgonTypography variant="caption" fontWeight="bold" display="block">Status</ArgonTypography>
                  <Chip label={detailItem.status || "Unknown"} color={getStatusColor(detailItem.status)} size="small" />
                </Grid>
                <Grid item xs={6}>
                  <ArgonTypography variant="caption" fontWeight="bold" display="block">Date</ArgonTypography>
                  <ArgonTypography variant="body2">{detailItem.date ? new Date(detailItem.date).toLocaleDateString() : "N/A"}</ArgonTypography>
                </Grid>
                <Grid item xs={12}>
                  <ArgonTypography variant="caption" fontWeight="bold" display="block">Description</ArgonTypography>
                  <ArgonTypography variant="body2">{detailItem.description || "N/A"}</ArgonTypography>
                </Grid>
                <Grid item xs={12}>
                  <ArgonTypography variant="caption" fontWeight="bold" display="block">Address</ArgonTypography>
                  <ArgonTypography variant="body2">{detailItem.address || "N/A"}</ArgonTypography>
                </Grid>
                {detailItem.severity && (
                  <Grid item xs={6}>
                    <ArgonTypography variant="caption" fontWeight="bold" display="block">Severity</ArgonTypography>
                    <ArgonTypography variant="body2">{detailItem.severity}</ArgonTypography>
                  </Grid>
                )}
                {detailItem.raw && (
                  <Grid item xs={12}>
                    <ArgonTypography variant="caption" fontWeight="bold" display="block" mb={1}>Raw NYC Data</ArgonTypography>
                    <ArgonBox
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRadius: "8px",
                        p: 2,
                        maxHeight: 300,
                        overflow: "auto",
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                      }}
                    >
                      <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(detailItem.raw, null, 2)}
                      </pre>
                    </ArgonBox>
                  </Grid>
                )}
              </Grid>
            </ArgonBox>
          )}
        </DialogContent>
        <DialogActions>
          <ArgonButton color="dark" size="small" onClick={() => setDetailOpen(false)}>Close</ArgonButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Violations;
